"use client";

import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { format, isValid } from "date-fns";
import { getUID } from "../../../../../Helper";

/* ========== Styled ========== */

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TabsBar = styled.div`
  display: flex;
  gap: 8px;
  margin: 2px 5px 5px;
  align-items: center;
`;

const TabButton = styled.button`
  border: 1px solid rgb(50, 55, 56);
  background: ${({ $active }) => ($active ? "rgb(50, 55, 56)" : "#000000")};
  color: ${({ $active }) => ($active ? "#f09c06" : "#ffffff")};
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: rgb(60, 65, 66);
    color: #f09c06;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  font-size: 12px;
  border-radius: 10px;
  background: #1a1a1a;
  border: 1px solid rgb(50, 55, 56);
  color: #cccccc;
`;

/* Secondary (MO / BM / FAN) */
const SubTabsBar = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  padding: 12px 0 8px;
  background: #171717;
  border: 1px solid rgb(50, 55, 56);
  border-radius: 8px;
  margin-bottom: 12px;
  margin-left: 5px;
`;

const SubTab = styled.button`
  background: ${({ $active }) => ($active ? "#f0a20c" : "transparent")};
  color: ${({ $active }) => ($active ? "#000000" : "#ffffff")};
  border: none;
  font-weight: 600;
  letter-spacing: 0.06em;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Montserrat Alternates', sans-serif;
  background: #000000;

  @media (max-width: 768px) {
    display: block;
    border: 0;
  }
`;

const TableHeader = styled.thead`
  background-color: rgb(50, 55, 56);
  color: rgb(240, 156, 6);

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  font-size: 14px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgb(50, 55, 56);
  background-color: ${(props) => {
    switch (props.$status) {
      case "won":
        return "#1a2a1a";
      case "lost":
        return "#2a1a1a";
      default:
        return "#000000";
    }
  }};

  &:hover {
    background-color: ${(props) => {
      switch (props.$status) {
        case "won":
          return "#2a3a2a";
        case "lost":
          return "#3a2a2a";
        default:
          return "rgb(50, 55, 56)";
      }
    }};
  }

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 15px;
    border-radius: 8px;
    padding: 12px;
    width: 113%;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #ffffff;

  @media (max-width: 768px) {
    display: block;
    text-align: right;
    padding: 8px 12px;
    font-size: 13px;

    &:before {
      content: attr(data-label);
      float: left;
      font-weight: bold;
      text-transform: capitalize;
      color: #f0a20c;
    }
  }
`;

const BetType = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.$type) {
      case "back":
        return "#e3f2fd";
      case "lay":
        return "#fce4ec";
      default:
        return "#f3e5f5";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "back":
        return "#1565c0";
      case "lay":
        return "#c2185b";
      default:
        return "#7b1fa2";
    }
  }};
`;

const Status = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.$status) {
      case "won":
        return "#1a2a1a";
      case "lost":
        return "#2a1a1a";
      default:
        return "rgb(50, 55, 56)";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "won":
        return "#4caf50";
      case "lost":
        return "#f44336";
      default:
        return "#cccccc";
    }
  }};
`;

const GameType = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: capitalize;
  color: #ffffff;

  @media (max-width: 768px) {
    display: block;
    text-align: right;

    &:before {
      content: attr(data-label);
      float: left;
    }
  }
`;

const BetDirection = styled.span`
  color: ${(props) => (props.$type === "back" ? "#4caf50" : "#f44336")};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/* ========== Component ========== */

export default function BettingHistoryTable() {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  // main tabs: open | closed | results
  const [activeMain, setActiveMain] = useState("open");

  // results sub-tabs: mo | bm | fan
  const [activeResultTab, setActiveResultTab] = useState("mo");

  // Results data from your APIs
  const [moResults, setMoResults] = useState([]);   // [{matchname, winteam, totalbets, payout, ...}]
  const [fanResults, setFanResults] = useState([]); // [{fancyname, selection, runsodds, payout, ...}]
  const [resultsLoading, setResultsLoading] = useState(false);
  const token = localStorage.getItem('token');
  useEffect(() => {
    const fetchBets = async () => {
      try {
        const uid = getUID();
        const res = await fetch(
          `https://api.ibitplay.com/sportsbetting/history/${uid}`,
          { headers: { 
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json" } 
          }
        );
        const data = await res.json();
        if (data && data.success) setBets(data.bets || []);
        else setBets([]);
      } catch (e) {
        console.error("Error fetching bet history:", e);
        setBets([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBets();
  }, []);

  // Fetch Results (MO + FAN) when Results section first opened
  useEffect(() => {
    const fetchResults = async () => {
      try {
        setResultsLoading(true);
        const uid = getUID();

        const [moRes, fanRes] = await Promise.all([
          fetch(`https://api.ibitplay.com/sportsbetting/MO/${uid}`, {
            headers: { 
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json" } ,
          }).then((r) => r.json()).catch(() => null),
          fetch(`https://api.ibitplay.com/sportsbetting/FAN/${uid}`, {
            headers: { 
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json" } ,
          }).then((r) => r.json()).catch(() => null),
        ]);

        // Normalize {data: {...}} vs {data:[...]} vs raw arrays
        const normData = (payload) => {
          if (!payload) return [];
          const d = payload.data ?? payload;
          if (Array.isArray(d)) return d;
          if (d && typeof d === "object") return [d];
          return [];
        };

        setMoResults(normData(moRes));
        setFanResults(normData(fanRes));
      } catch (e) {
        console.error("Error fetching results:", e);
        setMoResults([]);
        setFanResults([]);
      } finally {
        setResultsLoading(false);
      }
    };

    if (activeMain === "results" && moResults.length === 0 && fanResults.length === 0 && !resultsLoading) {
      fetchResults();
    }
  }, [activeMain, moResults.length, fanResults.length, resultsLoading]);

  /* ---------- helpers ---------- */

  const norm = (v) => (v || "").toString().toLowerCase();

  const openBets = useMemo(
    () => bets.filter((b) => norm(b.status) === "open"),
    [bets]
  );

  const closedBets = useMemo(
    () => bets.filter((b) => norm(b.status) !== "open"),
    [bets]
  );

  const visibleBets = activeMain === "open" ? openBets : closedBets;

  if (loading) {
    return <div style={{ color: "#fff", padding: "20px" }}>Loading...</div>;
  }

  /* ---------- Renders ---------- */

  const renderCommonHeader = () => (
    <TableHeader>
      <tr>
        <TableHeaderCell>Game</TableHeaderCell>
        <TableHeaderCell>Match</TableHeaderCell>
        <TableHeaderCell>Selection</TableHeaderCell>
        <TableHeaderCell>Type</TableHeaderCell>
        <TableHeaderCell>Odds</TableHeaderCell>
        <TableHeaderCell>Stake</TableHeaderCell>
        <TableHeaderCell>Liability</TableHeaderCell>
        <TableHeaderCell>Status</TableHeaderCell>
        <TableHeaderCell>Result</TableHeaderCell>
        <TableHeaderCell>Date</TableHeaderCell>
      </tr>
    </TableHeader>
  );

  const renderCommonRows = (list) => (
    <>
      {list.length > 0 ? (
        list.map((bet) => {
          const gameType = bet.game_type || "Unknown";
          const matchTitle = bet.match_title || bet.match_id || "Unknown Match";
          const betType = bet.bet_type || "fancy";
          const matchStartTime = bet.match_start_time || bet.created_at;
          const formattedDate = matchStartTime
            ? isValid(new Date(matchStartTime))
              ? format(new Date(matchStartTime), "MMM d, yyyy")
              : "N/A"
            : "N/A";
          const rowStatus = norm(bet.status);

          return (
            <TableRow
              key={bet.id ?? `${matchTitle}-${bet.selection_name}-${bet.created_at}`}
              $status={rowStatus}
            >
              <TableCell data-label="Game">
                <GameType>{matchTitle}</GameType>
              </TableCell>
              <TableCell data-label="Match">({gameType})</TableCell>
              <TableCell data-label="Selection">
                {bet.selection_name || "N/A"}
              </TableCell>
              <TableCell data-label="Type">
                <BetType $type={betType}>
                  {betType === "back" && (
                    <BetDirection $type="back">Back</BetDirection>
                  )}
                  {betType === "lay" && (
                    <BetDirection $type="lay">Lay</BetDirection>
                  )}
                  {betType === "fancy" && <span>Fancy</span>}
                </BetType>
              </TableCell>
              <TableCell data-label="Odds">{bet.odds ?? "N/A"}</TableCell>
              <TableCell data-label="Stake">INR {bet.stake ?? "N/A"}</TableCell>
              <TableCell data-label="Liability">
                INR {bet.liability ?? "N/A"}
              </TableCell>
              <TableCell data-label="Status">
                <Status $status={rowStatus}>
                  {rowStatus
                    ? rowStatus.charAt(0).toUpperCase() + rowStatus.slice(1)
                    : "Unknown"}
                </Status>
              </TableCell>
              <TableCell data-label="Result">
                {bet.result_status ?? "—"}
              </TableCell>
              <TableCell data-label="Date">{formattedDate}</TableCell>
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell colSpan={10} style={{ textAlign: "center", color: "#ccc" }}>
            No data
          </TableCell>
        </TableRow>
      )}
    </>
  );

  /* Results: FAN (from API) */
  const renderResultsFan = () => (
    <Table>
      <TableHeader>
        <tr>
          <TableHeaderCell>Matchname</TableHeaderCell>
          <TableHeaderCell>Type</TableHeaderCell>
          <TableHeaderCell>Runodds</TableHeaderCell>
          <TableHeaderCell>Payout</TableHeaderCell>
        </tr>
      </TableHeader>
      <tbody>
        {fanResults.length ? (
          fanResults.map((r, idx) => (
            <TableRow key={r.id ?? `fan-${idx}`}>
              {/* Using fancyname as "Matchname" (API doesn't provide matchname) */}
              <TableCell data-label="Matchname">
                {r.matchname || r.fancyname || "—"}
              </TableCell>
              <TableCell data-label="Type">
                {r.type || r.bet_type || r.selection || "—"}
              </TableCell>
              <TableCell data-label="Runodds">
                {r.runsodds ?? r.odds ?? "—"}
              </TableCell>
              <TableCell data-label="Payout">
                {r.payout != null ? `INR ${r.payout}` : "—"}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={4} style={{ textAlign: "center", color: "#ccc" }}>
              {resultsLoading ? "Loading..." : "No FAN results"}
            </TableCell>
          </TableRow>
        )}
      </tbody>
    </Table>
  );

  /* Results: MO (from API) */
  const renderResultsMO = () => (
    <Table>
      <TableHeader>
        <tr>
          <TableHeaderCell>Matchname</TableHeaderCell>
          <TableHeaderCell>Winteam</TableHeaderCell>
          <TableHeaderCell>Total Bets</TableHeaderCell>
          <TableHeaderCell>Final Payout</TableHeaderCell>
        </tr>
      </TableHeader>
      <tbody>
        {moResults.length ? (
          moResults.map((r, idx) => (
            <TableRow key={r.id ?? `mo-${idx}`}>
              <TableCell data-label="Matchname">
                {r.matchname || "—"}
              </TableCell>
              <TableCell data-label="Winteam">
                {r.winteam || r.match_result || "—"}
              </TableCell>
              <TableCell data-label="Total Bets">
                {r.totalbets ?? r.total_bets ?? "0"}
              </TableCell>
              <TableCell data-label="Final Payout">
                {`INR ${Number(r.payout ?? 0)}`}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={4} style={{ textAlign: "center", color: "#ccc" }}>
              {resultsLoading ? "Loading..." : "No MO results"}
            </TableCell>
          </TableRow>
        )}
      </tbody>
    </Table>
  );

  /* Results: BM placeholder (no API) */
  const renderResultsBM = () => (
    <Table>
      <TableHeader>
        <tr>
          <TableHeaderCell>Matchname</TableHeaderCell>
          <TableHeaderCell>Winteam</TableHeaderCell>
          <TableHeaderCell>Total Bets</TableHeaderCell>
          <TableHeaderCell>Final Payout</TableHeaderCell>
        </tr>
      </TableHeader>
      <tbody>
        <TableRow>
          <TableCell colSpan={4} style={{ textAlign: "center", color: "#ccc" }}>
            No BM results
          </TableCell>
        </TableRow>
      </tbody>
    </Table>
  );

  return (
    <>
      {/* Main Tabs */}
      <TabsBar>
        <TabButton
          $active={activeMain === "open"}
          onClick={() => setActiveMain("open")}
          aria-pressed={activeMain === "open"}
        >
          Open <CountBadge>{openBets.length}</CountBadge>
        </TabButton>
        <TabButton
          $active={activeMain === "closed"}
          onClick={() => setActiveMain("closed")}
          aria-pressed={activeMain === "closed"}
        >
          Closed <CountBadge>{closedBets.length}</CountBadge>
        </TabButton>
        <TabButton
          $active={activeMain === "results"}
          onClick={() => setActiveMain("results")}
          aria-pressed={activeMain === "results"}
        >
          Results
        </TabButton>
      </TabsBar>

      {/* Sections */}
      {activeMain === "results" ? (
        <>
          {/* MO / BM / FAN tab bar (selected = yellow) */}
          <SubTabsBar>
            <SubTab
              $active={activeResultTab === "mo"}
              onClick={() => setActiveResultTab("mo")}
            >
              MO
            </SubTab>
            <SubTab
              $active={activeResultTab === "bm"}
              onClick={() => setActiveResultTab("bm")}
            >
              BM
            </SubTab>
            <SubTab
              $active={activeResultTab === "fan"}
              onClick={() => setActiveResultTab("fan")}
            >
              FAN
            </SubTab>
          </SubTabsBar>

          <TableWrapper>
            {activeResultTab === "mo" && renderResultsMO()}
            {activeResultTab === "bm" && renderResultsBM()}
            {activeResultTab === "fan" && renderResultsFan()}
          </TableWrapper>
        </>
      ) : (
        <TableWrapper>
          <Table>
            {renderCommonHeader()}
            <tbody>
              {visibleBets.length > 0 ? (
                renderCommonRows(visibleBets)
              ) : (
                <TableRow>
                  <TableCell colSpan={10} style={{ textAlign: "center", color: "#ccc" }}>
                    {activeMain === "open" ? "No open bets" : "No closed bets"}
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </>
  );
}
